/**
 * Migration utility for moving from localStorage to IndexedDB
 * Handles backward compatibility and data migration
 */
import { getCache, type ServiceType } from './indexeddb-cache';
import { debugLog } from './debug-log';

// Migration status
export interface MigrationStatus {
  isRequired: boolean;
  isCompleted: boolean;
  migratedEntries: number;
  errors: string[];
}

// Migration configuration
const MIGRATION_CONFIG = {
  MIGRATION_KEY: 'cache_migration_status',
  MIGRATION_VERSION: '1.0',
  OLD_CACHE_PREFIX: 'usage_cache_',
  BATCH_SIZE: 10 // Process in batches to avoid blocking
};

/**
 * Check if migration is required
 */
export const checkMigrationRequired = (): boolean => {
  try {
    // Check if migration has already been completed
    const migrationStatus = localStorage.getItem(MIGRATION_CONFIG.MIGRATION_KEY);
    if (migrationStatus) {
      const status = JSON.parse(migrationStatus);
      if (status.version === MIGRATION_CONFIG.MIGRATION_VERSION && status.completed) {
        return false;
      }
    }

    // Check if there are any old cache entries
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(MIGRATION_CONFIG.OLD_CACHE_PREFIX)) {
        return true;
      }
    }

    return false;
  } catch (error) {
    console.error('Failed to check migration requirements:', error);
    return false;
  }
};

/**
 * Parse old cache key to extract service and endpoint information
 */
const parseOldCacheKey = (key: string): { service: ServiceType; endpoint: string; params: Record<string, unknown> } | null => {
  try {
    // Remove prefix: usage_cache_
    const keyWithoutPrefix = key.replace(MIGRATION_CONFIG.OLD_CACHE_PREFIX, '');
    
    // Parse different cache key formats
    if (keyWithoutPrefix.includes('organization_usage_details')) {
      // Format: organization_usage_details_2025-06-26_2025-06-27
      const parts = keyWithoutPrefix.split('_');
      if (parts.length >= 3) {
        const startDate = parts[parts.length - 2];
        const endDate = parts[parts.length - 1];
        return {
          service: 'openai',
          endpoint: 'organization_usage_details',
          params: { startDate, endDate }
        };
      }
    } else if (keyWithoutPrefix === 'projects') {
      return {
        service: 'openai',
        endpoint: 'projects',
        params: {}
      };
    } else if (keyWithoutPrefix === 'users') {
      return {
        service: 'openai',
        endpoint: 'users',
        params: {}
      };
    } else if (keyWithoutPrefix === 'organization_data') {
      return {
        service: 'openai',
        endpoint: 'organization_data',
        params: {}
      };
    }

    return null;
  } catch (error) {
    console.error(`Failed to parse old cache key: ${key}`, error);
    return null;
  }
};

/**
 * Migrate a single cache entry
 */
const migrateCacheEntry = async (key: string, value: string): Promise<boolean> => {
  try {
    const parsedKey = parseOldCacheKey(key);
    if (!parsedKey) {
      console.warn(`Skipping unknown cache key format: ${key}`);
      return false;
    }

    // Parse the old cache data
    const oldCacheData = JSON.parse(value);
    
    // Check if the old cache data has the expected structure
    if (!oldCacheData.value || !oldCacheData.timestamp) {
      console.warn(`Skipping invalid cache data for key: ${key}`);
      return false;
    }

    // Calculate remaining TTL
    const now = Date.now();
    const age = now - oldCacheData.timestamp;
    const originalTTL = oldCacheData.expiresIn || 3600000; // Default 1 hour
    const remainingTTL = Math.max(0, originalTTL - age);

    // Skip if already expired
    if (remainingTTL <= 0) {
      debugLog(`Skipping expired cache entry: ${key}`);
      return false;
    }

    // Migrate to IndexedDB
    const cache = getCache();
    const success = await cache.set(
      parsedKey.service,
      parsedKey.endpoint,
      oldCacheData.value,
      parsedKey.params,
      remainingTTL
    );

    if (success) {
      debugLog(`Migrated cache entry: ${key} -> ${parsedKey.service}_${parsedKey.endpoint}`);
    }

    return success;
  } catch (error) {
    console.error(`Failed to migrate cache entry ${key}:`, error);
    return false;
  }
};

/**
 * Perform cache migration
 */
export const performMigration = async (): Promise<MigrationStatus> => {
  const status: MigrationStatus = {
    isRequired: false,
    isCompleted: false,
    migratedEntries: 0,
    errors: []
  };

  try {
    // Check if migration is required
    if (!checkMigrationRequired()) {
      status.isCompleted = true;
      return status;
    }

    status.isRequired = true;
    debugLog('Starting cache migration from localStorage to IndexedDB...');

    // Collect all old cache entries
    const oldCacheEntries: { key: string; value: string }[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(MIGRATION_CONFIG.OLD_CACHE_PREFIX)) {
        const value = localStorage.getItem(key);
        if (value) {
          oldCacheEntries.push({ key, value });
        }
      }
    }

    debugLog(`Found ${oldCacheEntries.length} old cache entries to migrate`);

    // Migrate entries in batches
    for (let i = 0; i < oldCacheEntries.length; i += MIGRATION_CONFIG.BATCH_SIZE) {
      const batch = oldCacheEntries.slice(i, i + MIGRATION_CONFIG.BATCH_SIZE);
      
      await Promise.all(
        batch.map(async ({ key, value }) => {
          try {
            const success = await migrateCacheEntry(key, value);
            if (success) {
              status.migratedEntries++;
              // Remove the old cache entry after successful migration
              localStorage.removeItem(key);
            }
          } catch (error) {
            const errorMsg = `Failed to migrate ${key}: ${error instanceof Error ? error.message : 'Unknown error'}`;
            status.errors.push(errorMsg);
            console.error(errorMsg);
          }
        })
      );

      // Small delay between batches to avoid blocking
      if (i + MIGRATION_CONFIG.BATCH_SIZE < oldCacheEntries.length) {
        await new Promise(resolve => setTimeout(resolve, 10));
      }
    }

    // Mark migration as completed
    const migrationStatus = {
      version: MIGRATION_CONFIG.MIGRATION_VERSION,
      completed: true,
      completedAt: Date.now(),
      migratedEntries: status.migratedEntries,
      errors: status.errors
    };

    localStorage.setItem(MIGRATION_CONFIG.MIGRATION_KEY, JSON.stringify(migrationStatus));
    status.isCompleted = true;

    debugLog(`Cache migration completed: ${status.migratedEntries} entries migrated, ${status.errors.length} errors`);

    return status;
  } catch (error) {
    const errorMsg = `Migration failed: ${error instanceof Error ? error.message : 'Unknown error'}`;
    status.errors.push(errorMsg);
    console.error(errorMsg);
    return status;
  }
};

/**
 * Get migration status
 */
export const getMigrationStatus = (): MigrationStatus => {
  try {
    const migrationStatus = localStorage.getItem(MIGRATION_CONFIG.MIGRATION_KEY);
    if (migrationStatus) {
      const status = JSON.parse(migrationStatus);
      return {
        isRequired: false,
        isCompleted: status.completed || false,
        migratedEntries: status.migratedEntries || 0,
        errors: status.errors || []
      };
    }

    return {
      isRequired: checkMigrationRequired(),
      isCompleted: false,
      migratedEntries: 0,
      errors: []
    };
  } catch (error) {
    console.error('Failed to get migration status:', error);
    return {
      isRequired: false,
      isCompleted: false,
      migratedEntries: 0,
      errors: [`Failed to get migration status: ${error instanceof Error ? error.message : 'Unknown error'}`]
    };
  }
};

/**
 * Reset migration status (for testing purposes)
 */
export const resetMigrationStatus = (): boolean => {
  try {
    localStorage.removeItem(MIGRATION_CONFIG.MIGRATION_KEY);
    debugLog('Migration status reset');
    return true;
  } catch (error) {
    console.error('Failed to reset migration status:', error);
    return false;
  }
};

/**
 * Clean up old cache entries (use with caution)
 */
export const cleanupOldCache = (): number => {
  let cleanedCount = 0;
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(MIGRATION_CONFIG.OLD_CACHE_PREFIX)) {
        localStorage.removeItem(key);
        cleanedCount++;
      }
    }
    debugLog(`Cleaned up ${cleanedCount} old cache entries`);
  } catch (error) {
    console.error('Failed to clean up old cache:', error);
  }
  return cleanedCount;
};